import React from 'react';
import { FontWeight, Heading, LiliTooltip, LiliToolTipColor, Text, TooltipPosition } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { RoleType } from '../../../../types';
import { Layout } from '../../../../components/Layout/Layout';
import { InviteStepHeader } from '../../components/InviteStepHeader';
import { InviteOptionItem, InviteOptionItemProps } from './components/InviteOptionItem';
import { InviteStepFooter } from '../../components/InviteStepFooter';
import { ReactComponent as TeamIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-team.svg';
import { ReactComponent as AdminIcon } from '../../../../assets/icons/svg/shared/ic-shared-add-admin.svg';
import { ReactComponent as AccountantIcon } from '../../../../assets/icons/svg/shared/ic-shared-add-accountant.svg';
import { ReactComponent as AlertIcon } from '../../../../assets/icons/svg/shared/ic-shared-alert.svg';
import { useRoleSelectionPageLogic } from './hooks/useRoleSelectionPageLogic';
import { trackMixPanel } from '../../../../services/mixpanel.service';
import { VIEWS_MAP } from '../../../../router/constants';
import { PermissionsList } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

export const RoleSelectionStep = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('inviteEN');
  const {
    onSelectRole,
    selectedRole,
    onSubmit,
    allowAdminInvite,
    showAdminOption,
    allowAccountantInvite,
    showAccountantOption,
    learnMoreLink,
  } = useRoleSelectionPageLogic();
  const [permissionsTypeToShow, setPermissionsTypeToShow] = React.useState<RoleType | null>(null);

  const onRoleClick = (role: RoleType) => {
    trackMixPanel('Act on choose team member role', { Role: role });
    onSelectRole(role);
  };

  const options: InviteOptionItemProps[] = [
    {
      title: t('INVITE_INTRO.ROLES_LIST.ADMIN.TITLE'),
      description: t('INVITE_INTRO.ROLES_LIST.ADMIN.DESCRIPTION'),
      icon: <AdminIcon className={twMerge(allowAdminInvite ? 'text-text-main-primary' : 'text-text-main-tertiary')} />,
      isSelected: true,
      type: RoleType.ADMIN,
      onSelect: onRoleClick,
      disabled: !allowAdminInvite,
      hidden: !showAdminOption,
      onInfoClick: setPermissionsTypeToShow,
    },
    {
      title: t('INVITE_INTRO.ROLES_LIST.ACCOUNTANT.TITLE'),
      description: t('INVITE_INTRO.ROLES_LIST.ACCOUNTANT.DESCRIPTION'),
      icon: (
        <AccountantIcon
          className={twMerge(allowAccountantInvite ? 'text-text-main-primary' : 'text-text-main-tertiary')}
        />
      ),
      isSelected: false,
      type: RoleType.ACCOUNTANT,
      onSelect: onRoleClick,
      disabled: !allowAccountantInvite,
      hidden: !showAccountantOption,
      onInfoClick: setPermissionsTypeToShow,
    },
  ];

  return (
    <Layout
      shouldHideProfile
      shouldHideChatBot
      childrenWrapperClassName='h-full w-full flex flex-col justify-between  fixed'
      wrapperClassName='w-full h-full bg-bw01 overflow-none z-[9999] bg-surface-primary-bw-00'
    >
      <div className='  overflow-none  '>
        <InviteStepHeader onBackClick={() => navigate(`/${VIEWS_MAP.ACCOUNT_SETTINGS}`)} />

        <div className='w-full flex flex-col items-center  mt-[58px] '>
          <TeamIcon />
          <div className='w-[617px] px-[60px]'>
            <Heading.H4 fontWeight={FontWeight.MEDIUM} className='text-center text-text-main-primary mt-[48px]'>
              {t('INVITE_INTRO.TITLE')}
            </Heading.H4>
            <p className='text-center text-text-main-secondary font-metricR text-[16px]'>
              {t('INVITE_INTRO.DESCRIPTION.PART_1')}
              <br />
              <a target='__blank' href={learnMoreLink} className='text-text-brand-primary underline'>
                {t('INVITE_INTRO.DESCRIPTION.PART_2')}
              </a>
              {t('INVITE_INTRO.DESCRIPTION.PART_3')}
            </p>
            <div className='flex flex-row items-center border-action01 border-[1px] bg-action00 px-[12px] py-[8px] rounded-lg  mt-[24px] gap-2'>
              <AlertIcon className='w-[24px] h-[24px]  text-text-brand-primary' />
              <Text.H10 fontWeight={FontWeight.REGULAR} className='w-full text-text-main-primary'>
                {t('INVITE_INTRO.DISCLAIMER')}
              </Text.H10>
            </div>
            <p className='text-bw08 font-metricR text-[16px] mt-[30px] mb-[8px] text-text-main-secondary'>
              {t('INVITE_INTRO.ROLES_TITLE')}
            </p>
            <div className='flex flex-col gap-4'>
              {options.map((option) => (
                <InviteOptionItem
                  key={option.type}
                  {...option}
                  isSelected={selectedRole === option.type}
                  onInfoClick={setPermissionsTypeToShow}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {!allowAdminInvite && (
        <LiliTooltip
          description={t('INVITE_INTRO.ROLES_LIST.ADMIN.MAX_LIMIT_TOOLTIP')}
          targetId={`invite-disabled-tooltip-${RoleType.ADMIN}`}
          position={TooltipPosition.RIGHT}
          color={LiliToolTipColor.White}
          className='max-w-[264px]'
        />
      )}

      {!allowAccountantInvite && (
        <LiliTooltip
          description={t('INVITE_INTRO.ROLES_LIST.ACCOUNTANT.MAX_LIMIT_TOOLTIP')}
          targetId={`invite-disabled-tooltip-${RoleType.ACCOUNTANT}`}
          position={TooltipPosition.RIGHT}
          color={LiliToolTipColor.White}
          className='max-w-[264px]'
        />
      )}
      <InviteStepFooter onClick={onSubmit} buttonTitle='Continue' isLoading={false} disabled={!selectedRole} />
      {permissionsTypeToShow && (
        <PermissionsList onClose={() => setPermissionsTypeToShow(null)} type={permissionsTypeToShow} />
      )}
    </Layout>
  );
};
